import React, { createContext, useContext, useReducer } from 'react';
import { ticketNameQualifiesForCircusBundleDiscount } from '../utils/circusBundleDiscount';

const FLOWER_GARDEN_PAGE_ID = '1ced1e1a-b442-4dee-b1e1-ae8b5b7bee09';
const GROTTO_RETAIL_VILLAGE_PAGE_ID = '9afa8eb9-dd32-4529-b9af-316e13a7397e';
// Summer Solstice 2026: fixed-price accommodation packages — group discount inapplicable
const SUMMER_SOLSTICE_PAGE_ID = '0efbadd8-388c-4ad0-ba36-c88282ac634d';

// Page IDs that should not have Family Saver discount
const NO_FAMILY_SAVER_PAGE_IDS = [
  FLOWER_GARDEN_PAGE_ID,
  GROTTO_RETAIL_VILLAGE_PAGE_ID,
  SUMMER_SOLSTICE_PAGE_ID,
];

// Initial state
const initialState = {
  // Booking flow state
  currentStep: 1,
  totalSteps: 6,
  
  // Location and page configuration
  locationId: '978',
  pageId: null,
  // Family Saver: when false, discount/code are disabled (from Security Center page config)
  familySaverEnabled: true,
  
  // Selected tickets and quantities
  selectedTickets: [],
  
  // Selected date and time
  selectedDate: null,
  selectedTimeSlot: null,
  
  // Upsells
  availableUpsells: [],
  selectedUpsells: [],
  
  // Modifier selections (stored locally until checkout)
  modifierSelections: {}, // { webSaleItemId: { modifierId, modifierTextId, modifierName } }

  // Vineyard tour dietary requirements — array of { choice, notes } per guest
  vineyardDietaryRequirements: [],
  
  // Customer information
  customerDetails: {
    firstName: '',
    lastName: '',
    email: '',
    confirmEmail: '',
    phone: '',
    contactable: false,
    address: {
      line1: '',
      line2: '',
      city: '',
      postcode: '',
      country: 'GB'
    }
  },
  
  // Payment information
  paymentDetails: {
    method: 'payment_sense', // card, payment_sense, paypal, apple_pay, google_pay
    cardDetails: {
      number: '',
      expiryMonth: '',
      expiryYear: '',
      cvv: '',
      name: ''
    }
  },
  
  // Booking summary
  bookingSummary: {
    totalAmount: 0,
    bookingReference: null,
    confirmationEmail: null
  },
  
  // UI state
  loading: false,
  error: null,
  validationErrors: {}
};

// Action types
export const BOOKING_ACTIONS = {
  // Flow control
  SET_CURRENT_STEP: 'SET_CURRENT_STEP',
  NEXT_STEP: 'NEXT_STEP',
  PREV_STEP: 'PREV_STEP',
  RESET_BOOKING: 'RESET_BOOKING',
  
  // Configuration
  SET_LOCATION_ID: 'SET_LOCATION_ID',
  SET_PAGE_ID: 'SET_PAGE_ID',
  SET_FAMILY_SAVER_ENABLED: 'SET_FAMILY_SAVER_ENABLED',
  
  // Tickets
  SET_SELECTED_TICKETS: 'SET_SELECTED_TICKETS',
  UPDATE_TICKET_QUANTITY: 'UPDATE_TICKET_QUANTITY',
  ADD_TICKET: 'ADD_TICKET',
  REMOVE_TICKET: 'REMOVE_TICKET',
  
  // Date and time
  SET_SELECTED_DATE: 'SET_SELECTED_DATE',
  SET_SELECTED_TIME_SLOT: 'SET_SELECTED_TIME_SLOT',
  
  // Upsells
  SET_AVAILABLE_UPSELLS: 'SET_AVAILABLE_UPSELLS',
  SET_SELECTED_UPSELLS: 'SET_SELECTED_UPSELLS',
  UPDATE_UPSELL_QUANTITY: 'UPDATE_UPSELL_QUANTITY',
  
  // Modifiers
  SET_MODIFIER_SELECTION: 'SET_MODIFIER_SELECTION',
  CLEAR_MODIFIER_SELECTIONS: 'CLEAR_MODIFIER_SELECTIONS',

  // Vineyard tour dietary
  SET_VINEYARD_DIETARY: 'SET_VINEYARD_DIETARY',
  
  // Customer details
  UPDATE_CUSTOMER_DETAILS: 'UPDATE_CUSTOMER_DETAILS',
  UPDATE_CUSTOMER_ADDRESS: 'UPDATE_CUSTOMER_ADDRESS',
  
  // Payment
  UPDATE_PAYMENT_DETAILS: 'UPDATE_PAYMENT_DETAILS',
  UPDATE_CARD_DETAILS: 'UPDATE_CARD_DETAILS',
  
  // Booking summary
  SET_BOOKING_SUMMARY: 'SET_BOOKING_SUMMARY',
  SET_BOOKING_REFERENCE: 'SET_BOOKING_REFERENCE',
  
  // UI state
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_VALIDATION_ERRORS: 'SET_VALIDATION_ERRORS',
  CLEAR_ERROR: 'CLEAR_ERROR'
};

// Reducer
const bookingReducer = (state, action) => {
  switch (action.type) {
    case BOOKING_ACTIONS.SET_CURRENT_STEP:
      return {
        ...state,
        currentStep: action.payload,
        error: null
      };
      
    case BOOKING_ACTIONS.NEXT_STEP:
      return {
        ...state,
        currentStep: Math.min(state.currentStep + 1, state.totalSteps),
        error: null
      };
      
    case BOOKING_ACTIONS.PREV_STEP:
      return {
        ...state,
        currentStep: Math.max(state.currentStep - 1, 1),
        error: null
      };
      
    case BOOKING_ACTIONS.RESET_BOOKING:
      return {
        ...initialState,
        locationId: state.locationId, // Keep location ID
        pageId: state.pageId // Keep page ID
      };
      
    case BOOKING_ACTIONS.SET_LOCATION_ID:
      return {
        ...state,
        locationId: action.payload
      };
      
    case BOOKING_ACTIONS.SET_PAGE_ID:
      return {
        ...state,
        pageId: action.payload,
        familySaverEnabled: true // Reset to default until page config is fetched
      };
      
    case BOOKING_ACTIONS.SET_FAMILY_SAVER_ENABLED:
      return {
        ...state,
        familySaverEnabled: action.payload
      };
      
    case BOOKING_ACTIONS.SET_SELECTED_TICKETS:
      return {
        ...state,
        selectedTickets: action.payload
      };
      
    case BOOKING_ACTIONS.UPDATE_TICKET_QUANTITY:
      return {
        ...state,
        selectedTickets: state.selectedTickets.map(ticket =>
          ticket.webSaleItemId === action.payload.ticketId
            ? { ...ticket, quantity: Math.max(0, action.payload.quantity) }
            : ticket
        )
      };
      
    case BOOKING_ACTIONS.ADD_TICKET:
      const existingTicket = state.selectedTickets.find(
        ticket => ticket.webSaleItemId === action.payload.webSaleItemId
      );
      
      if (existingTicket) {
        return {
          ...state,
          selectedTickets: state.selectedTickets.map(ticket =>
            ticket.webSaleItemId === action.payload.webSaleItemId
              ? { ...ticket, quantity: ticket.quantity + 1 }
              : ticket
          )
        };
      } else {
        return {
          ...state,
          selectedTickets: [...state.selectedTickets, { ...action.payload, quantity: 1 }]
        };
      }
      
    case BOOKING_ACTIONS.REMOVE_TICKET:
      return {
        ...state,
        selectedTickets: state.selectedTickets.filter(
          ticket => ticket.webSaleItemId !== action.payload
        )
      };
      
    case BOOKING_ACTIONS.SET_SELECTED_DATE:
      return {
        ...state,
        selectedDate: action.payload
      };
      
    case BOOKING_ACTIONS.SET_SELECTED_TIME_SLOT:
      return {
        ...state,
        selectedTimeSlot: action.payload
      };
      
    case BOOKING_ACTIONS.SET_AVAILABLE_UPSELLS:
      return {
        ...state,
        availableUpsells: action.payload
      };
      
    case BOOKING_ACTIONS.SET_SELECTED_UPSELLS:
      return {
        ...state,
        selectedUpsells: action.payload
      };
      
    case BOOKING_ACTIONS.UPDATE_UPSELL_QUANTITY:
      return {
        ...state,
        selectedUpsells: state.selectedUpsells.map(upsell =>
          upsell.id === action.payload.upsellId
            ? { ...upsell, quantity: Math.max(0, action.payload.quantity) }
            : upsell
        )
      };
      
    case BOOKING_ACTIONS.UPDATE_CUSTOMER_DETAILS:
      return {
        ...state,
        customerDetails: {
          ...state.customerDetails,
          ...action.payload
        }
      };
      
    case BOOKING_ACTIONS.UPDATE_CUSTOMER_ADDRESS:
      return {
        ...state,
        customerDetails: {
          ...state.customerDetails,
          address: {
            ...state.customerDetails.address,
            ...action.payload
          }
        }
      };
      
    case BOOKING_ACTIONS.UPDATE_PAYMENT_DETAILS:
      return {
        ...state,
        paymentDetails: {
          ...state.paymentDetails,
          ...action.payload
        }
      };
      
    case BOOKING_ACTIONS.UPDATE_CARD_DETAILS:
      return {
        ...state,
        paymentDetails: {
          ...state.paymentDetails,
          cardDetails: {
            ...state.paymentDetails.cardDetails,
            ...action.payload
          }
        }
      };
      
    case BOOKING_ACTIONS.SET_MODIFIER_SELECTION:
      return {
        ...state,
        modifierSelections: {
          ...state.modifierSelections,
          [action.payload.webSaleItemId]: action.payload.modifierData
        }
      };
      
    case BOOKING_ACTIONS.CLEAR_MODIFIER_SELECTIONS:
      return {
        ...state,
        modifierSelections: {}
      };

    case BOOKING_ACTIONS.SET_VINEYARD_DIETARY:
      return {
        ...state,
        vineyardDietaryRequirements: action.payload
      };
      
    case BOOKING_ACTIONS.SET_BOOKING_SUMMARY:
      return {
        ...state,
        bookingSummary: {
          ...state.bookingSummary,
          ...action.payload
        }
      };
      
    case BOOKING_ACTIONS.SET_BOOKING_REFERENCE:
      return {
        ...state,
        bookingSummary: {
          ...state.bookingSummary,
          bookingReference: action.payload
        }
      };
      
    case BOOKING_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
      
    case BOOKING_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };
      
    case BOOKING_ACTIONS.SET_VALIDATION_ERRORS:
      return {
        ...state,
        validationErrors: action.payload
      };
      
    case BOOKING_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
        validationErrors: {}
      };
      
    default:
      return state;
  }
};

// Context
const BookingContext = createContext();

// Provider component
export const BookingProvider = ({ children }) => {
  const [state, dispatch] = useReducer(bookingReducer, initialState);

  // Helper functions
  const getSubtotal = () => {
    return state.selectedTickets.reduce((total, ticket) => {
      return total + (ticket.price * ticket.quantity);
    }, 0);
  };

  const getGroupDiscountAmount = () => {
    // Disable when Security Center has Family Saver turned off for this page
    if (state.familySaverEnabled === false) {
      return 0;
    }
    // Disable Family Saver pricing for Flower and Garden Show 2026 and Grotto Retail Village
    if (NO_FAMILY_SAVER_PAGE_IDS.includes(state.pageId)) {
      return 0;
    }
    
    // Family Saver does NOT apply to membership journeys
    // Check if all tickets are memberships (webSaleItemTypeID === 4)
    const hasMembershipTickets = state.selectedTickets.some(t => t.webSaleItemTypeID === 4);
    const allTicketsAreMemberships = state.selectedTickets.length > 0 && 
                                     state.selectedTickets.every(t => t.webSaleItemTypeID === 4 || t.quantity === 0);
    
    if (hasMembershipTickets && allTicketsAreMemberships) {
      return 0; // No discount for membership journeys
    }
    
    // Only count MAIN tickets (Step 1) for family saver calculation - exclude ALL upsells
    const mainPaidTickets = state.selectedTickets.filter(t => {
      // Exclude if ANY of these conditions indicate it's an upsell
      const isUpsell = t.isUpsellItem || 
                       t.pageType === 'EventTemplate' || 
                       t.parentUpsellId || 
                       t.parentUpsellName;
      
      // Exclude membership tickets (webSaleItemTypeID === 4)
      const isMembership = t.webSaleItemTypeID === 4;
      
      const hasPrice = (Number(t.price) || 0) > 0;
      
      // Only include if it has a price AND is NOT an upsell AND is NOT a membership
      return hasPrice && !isUpsell && !isMembership;
    });
    
    const paidTicketCount = mainPaidTickets.reduce((sum, t) => sum + (t.quantity || 0), 0);
    
    // Calculate subtotal for MAIN tickets only (discount should not apply to upsells)
    const mainTicketSubtotal = mainPaidTickets.reduce((total, ticket) => {
      return total + (ticket.price * ticket.quantity);
    }, 0);
    
    if (mainTicketSubtotal <= 0 || paidTicketCount < 2) return 0;

    // Flexible Family Saver tiers (do not expose percentages to users)
    // 2: 3%, 3: 5%, 4: 10%, 5-9: 15%, 10+: 20%
    let rate = 0;
    if (paidTicketCount >= 10) rate = 0.20;
    else if (paidTicketCount >= 5) rate = 0.15;
    else if (paidTicketCount === 4) rate = 0.10;
    else if (paidTicketCount === 3) rate = 0.05;
    else if (paidTicketCount === 2) rate = 0.03;

    const discountAmount = Math.round((mainTicketSubtotal * rate) * 100) / 100;
    
    console.log('💰 Family Saver Discount:', 
      'Subtotal:', mainTicketSubtotal,
      'Count:', paidTicketCount,
      'Rate:', `${rate * 100}%`,
      'Discount:', discountAmount
    );

    return discountAmount;
  };

  const hasCircusTicketsForDiscount = () => {
    return state.selectedTickets.some(t => {
      const qty = parseInt(t.quantity, 10) || 0;
      if (qty <= 0) return false;
      return ticketNameQualifiesForCircusBundleDiscount(t.name);
    });
  };

  const calculateCircusDiscountAmount = () => {
    // Only calculate if user has qualifying circus tickets
    if (!hasCircusTicketsForDiscount()) {
      return { amount: 0, eligibleItemsCount: 0 };
    }

    // Calculate 20% of eligible items and count total quantity
    // EXCLUDE: Afternoon Tea products, Circus tickets, Circus Workshops
    let eligibleTotal = 0;
    let eligibleItemsCount = 0;
    
    state.selectedTickets.forEach(ticket => {
      const name = (ticket.name || '').toLowerCase();
      const price = parseFloat(ticket.price) || 0;
      const quantity = parseInt(ticket.quantity) || 0;
      
      // Exclude products with 0 price or 0 quantity
      if (price <= 0 || quantity <= 0) {
        console.log('🎪 [CIRCUS DISCOUNT] Excluding zero-price/quantity item:', ticket.name);
        return;
      }
      
      // Exclude Afternoon Tea products
      if (name.includes('afternoon tea')) {
        console.log('🎪 [CIRCUS DISCOUNT] Excluding Afternoon Tea:', ticket.name);
        return;
      }
      
      // Exclude Circus tickets (they already got the Circus event, don't discount it)
      if (name.includes('circus') || name.includes('workshop')) {
        console.log('🎪 [CIRCUS DISCOUNT] Excluding Circus/Workshop:', ticket.name);
        return;
      }
      
      // Include this item in 20% calculation
      const itemTotal = price * quantity;
      console.log('🎪 [CIRCUS DISCOUNT] Including:', ticket.name, '£' + itemTotal.toFixed(2));
      eligibleTotal += itemTotal;
      eligibleItemsCount += quantity;
    });
    
    const discountAmount = eligibleTotal * 0.2; // 20% off
    
    console.log('🎪 [CIRCUS DISCOUNT] Calculation:', {
      eligibleTotal: '£' + eligibleTotal.toFixed(2),
      discountAmount: '£' + discountAmount.toFixed(2),
      eligibleItemsCount: eligibleItemsCount,
      percentage: '20%'
    });
    
    return { amount: discountAmount, eligibleItemsCount: eligibleItemsCount };
  };

  const getFamilySaverCode = () => {
    // Disable when Security Center has Family Saver turned off for this page
    if (state.familySaverEnabled === false) {
      console.log('🎟️ Family Saver disabled: Security Center has Family Saver disabled for this page');
      return null;
    }
    // Disable Family Saver codes for Flower and Garden Show 2026 and Grotto Retail Village
    if (NO_FAMILY_SAVER_PAGE_IDS.includes(state.pageId)) {
      console.log('🎟️ Family Saver disabled:', state.pageId === FLOWER_GARDEN_PAGE_ID ? 'Flower and Garden Show 2026 page' : 'Grotto Retail Village page');
      return null;
    }
    
    // Family Saver does NOT apply to membership journeys
    // Check if all tickets are memberships (webSaleItemTypeID === 4)
    const hasMembershipTickets = state.selectedTickets.some(t => t.webSaleItemTypeID === 4);
    const allTicketsAreMemberships = state.selectedTickets.length > 0 && 
                                     state.selectedTickets.every(t => t.webSaleItemTypeID === 4 || t.quantity === 0);
    
    if (hasMembershipTickets && allTicketsAreMemberships) {
      console.log('🎟️ Family Saver disabled: Membership journey detected');
      return null;
    }
    
    // Only count MAIN tickets (Step 1) for family saver code determination - exclude ALL upsells
    const mainPaidTickets = state.selectedTickets.filter(t => {
      // Exclude if ANY of these conditions indicate it's an upsell
      const isUpsell = t.isUpsellItem || 
                       t.pageType === 'EventTemplate' || 
                       t.parentUpsellId || 
                       t.parentUpsellName;
      
      // Exclude membership tickets (webSaleItemTypeID === 4)
      const isMembership = t.webSaleItemTypeID === 4;
      
      const hasPrice = (Number(t.price) || 0) > 0;
      
      // Only include if it has a price AND is NOT an upsell AND is NOT a membership
      return hasPrice && !isUpsell && !isMembership;
    });
    
    const paidTicketCount = mainPaidTickets.reduce((sum, t) => sum + (t.quantity || 0), 0);
    
    // Debug logging to verify correct filtering
    console.log('🎟️ Family Saver Calculation:', {
      totalTickets: state.selectedTickets.length,
      mainTickets: mainPaidTickets.map(t => ({ name: t.name, qty: t.quantity, price: t.price })),
      mainTicketCount: paidTicketCount,
      excludedUpsells: state.selectedTickets.filter(t => 
        t.isUpsellItem || t.pageType === 'EventTemplate' || t.parentUpsellId
      ).map(t => ({ name: t.name, qty: t.quantity })),
      excludedMemberships: state.selectedTickets.filter(t => t.webSaleItemTypeID === 4).map(t => ({ name: t.name, qty: t.quantity }))
    });

    if (paidTicketCount >= 10) return 'FamilySaver10';
    else if (paidTicketCount >= 5) return 'FamilySaver5';
    else if (paidTicketCount === 4) return 'FamilySaver4';
    else if (paidTicketCount === 3) return 'FamilySaver3';
    else if (paidTicketCount === 2) return 'FamilySaver2';
    return null;
  };

  const calculateTotal = () => {
    const subtotal = getSubtotal();
    const discount = getGroupDiscountAmount();
    return Math.max(0, subtotal - discount);
  };

  const getSelectedTicketsCount = () => {
    return state.selectedTickets.reduce((total, ticket) => total + ticket.quantity, 0);
  };

  const canProceedToNextStep = () => {
    switch (state.currentStep) {
      case 1: // Ticket selection
        return state.selectedTickets.some(ticket => ticket.quantity > 0);
      case 2: // Date selection
        return state.selectedDate !== null && state.selectedTimeSlot !== null;
      case 3: // Upsells - allow proceeding even if no upsells selected
        return true;
      case 4: { // Customer details
        const email = state.customerDetails.email?.trim();
        const confirmEmail = state.customerDetails.confirmEmail?.trim();
        return state.customerDetails.firstName && 
               state.customerDetails.lastName && 
               email &&
               confirmEmail &&
               email.toLowerCase() === confirmEmail.toLowerCase();
      }
      case 5: // Payment
        return state.paymentDetails.method === 'card' ? 
          state.paymentDetails.cardDetails.number && 
          state.paymentDetails.cardDetails.expiryMonth &&
          state.paymentDetails.cardDetails.expiryYear &&
          state.paymentDetails.cardDetails.cvv &&
          state.paymentDetails.cardDetails.name :
          true; // For Payment Sense and other methods, allow proceeding
      default:
        return false;
    }
  };

  const value = {
    ...state,
    dispatch,
    calculateTotal,
    getSubtotal,
    getGroupDiscountAmount,
    getFamilySaverCode,
    hasCircusTicketsForDiscount,
    calculateCircusDiscountAmount,
    getSelectedTicketsCount,
    canProceedToNextStep
  };

  return (
    <BookingContext.Provider value={value}>
      {children}
    </BookingContext.Provider>
  );
};

// Hook to use booking context
export const useBooking = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
};

export default BookingContext;